import React, { useEffect, useState } from "react";
import { Kbd } from "../../../shared/shortcuts/ShortcutHelp.jsx";
import {
  DEFAULT_SHORTCUTS,
  SHORTCUT_CATEGORIES,
  SHORTCUTS_CHANGED_EVENT,
  comboFromEvent,
  findConflict,
  getCombo,
  isCustomized,
  resetAllShortcuts,
  resetShortcut,
  saveShortcutOverride
} from "../../../shared/shortcuts/registry.js";

/**
 * Desktop keyboard-shortcut list with per-action customisation (saas-books ShortcutsSettingsTab):
 * "Change" records the next key press (Esc cancels, bare modifiers ignored, clashes rejected).
 */
export function ShortcutsSettings() {
  const [, setVersion] = useState(0);
  const [filter, setFilter] = useState("");
  const [recordingId, setRecordingId] = useState(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const onChange = () => setVersion((v) => v + 1);
    window.addEventListener(SHORTCUTS_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(SHORTCUTS_CHANGED_EVENT, onChange);
  }, []);

  useEffect(() => {
    if (!recordingId) return undefined;
    // Capture phase so the key never reaches the app's own shortcut handlers
    const onKeyDown = (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (e.key === "Escape") {
        setRecordingId(null);
        return;
      }
      const combo = comboFromEvent(e);
      if (!combo) return; // bare modifier — keep listening
      const clash = findConflict(combo, recordingId);
      if (clash) {
        setMessage(`"${combo}" is already used for "${clash.label}".`);
        return;
      }
      saveShortcutOverride(recordingId, combo);
      setMessage("");
      setRecordingId(null);
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [recordingId]);

  const term = filter.trim().toLowerCase();
  const rows = DEFAULT_SHORTCUTS.filter((s) => !term || s.label.toLowerCase().includes(term) || s.category.toLowerCase().includes(term));

  return (
    <div className="shortcut-settings">
      <div className="shortcut-settings-bar">
        <input
          type="text"
          className="form-control"
          placeholder="Filter shortcuts…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => {
            resetAllShortcuts();
            setMessage("");
          }}
        >
          Reset all to defaults
        </button>
      </div>
      {message && <div className="form-error">{message}</div>}

      {SHORTCUT_CATEGORIES.map((category) => {
        const items = rows.filter((s) => s.category === category);
        if (!items.length) return null;
        return (
          <section key={category} className="shortcut-help-group">
            <h4 className="shortcut-help-title">{category}</h4>
            {items.map((s) => (
              <div key={s.id} className="shortcut-help-row">
                <span>
                  {s.label}
                  {isCustomized(s.id) && <span className="shortcut-custom-tag">custom</span>}
                </span>
                <span className="shortcut-row-actions">
                  {recordingId === s.id ? <span className="shortcut-recording">Press keys… (Esc to cancel)</span> : <Kbd combo={getCombo(s.id)} />}
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      setMessage("");
                      setRecordingId(recordingId === s.id ? null : s.id);
                    }}
                  >
                    {recordingId === s.id ? "Cancel" : "Change"}
                  </button>
                  {isCustomized(s.id) && (
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => resetShortcut(s.id)}>
                      Reset
                    </button>
                  )}
                </span>
              </div>
            ))}
          </section>
        );
      })}
    </div>
  );
}

export default ShortcutsSettings;
